import { isDraft, original } from '@reduxjs/toolkit';
import { copyWithStructuralSharing } from '@reduxjs/toolkit/query';
import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { PaginatedApiResponse } from 'domain/Common';
import { isEqual } from 'lodash-es';
import { axiosBaseQuery, getApiBaseUrl } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';
import { getOrCreateVisitorId } from 'pages/Natalie/telemetry/visitorId';

import { extractCtxParams } from './ctxParams';
import {
  mapAccountlessV2EventFromApi,
  mapNatalieEventDetailFromApi,
  mapNatalieV2EventListFromApi,
  mapNatalieV2PublicEventFromApi,
} from './mappers';
import { NO_RESPONSE_STATUS, mapCreatedNatalieDraftFromApi, mapNatalieDraftFromApi } from './natalieCreateV2.mappers';

import type { CreatedNatalieDraft, NatalieDraft } from './natalieCreateV2.mappers';
import type {
  NatalieEventV2CreateDto,
  NatalieEventV2DraftUpdateDto,
  NatalieEventV2Dto,
  NatalieEventV2GenerateDto,
  NatalieEventV2UpdateDto,
  NatalieVoiceListDto,
  NatalieVoicePreviewDto,
  NatalieVoicePreviewRequestDto,
} from './natalieCreateV2.types';
import type {
  AccountlessNatalieEventV2ApiResponse,
  CreateNatalieEventApiParams,
  GetAccountlessLiveKitTokenParams,
  GetLiveKitTokenData,
  GetLiveKitTokenParams,
  GetNatalieConversationsArgs,
  GetNatalieEventListApiParams,
  NatalieConversationsPage,
  NatalieEventDetail,
  NatalieEventDto,
  NatalieEventListItem,
  NatalieEventMetrics,
  NatalieEventPublicDetail,
  NotifyNatalieIntakeFormArgs,
  NotifyNatalieIntakeFormResponse,
  RecordAccountlessVisitArgs,
  RegisterToNatalieEventArgs,
  RequestNatalieCreateEventArgs,
  RequestNatalieCreateEventResponse,
  ScheduleAccountlessReminderArgs,
  SetNatalieInvitedCountArgs,
  UpdateNatalieEventApiParams,
} from './natalieEvent.types';
import type {
  CreateNatalieReportArgs,
  DeleteNatalieReportArgs,
  GetNatalieReportListApiParams,
  GetNatalieReportArgs,
  NatalieReport,
  NatalieReportListItem,
  RegenerateNatalieReportArgs,
  RestoreNatalieReportVersionArgs,
  RestoreNatalieReportVersionResponse,
} from './natalieReport.types';
import type { Draft } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

// natalie-api base queries (new standalone service)
const natalieApiBaseQuery = axiosBaseQuery({ baseURL: () => configService.getUrl(UrlNames.NatalieApi) });

// Backend-api base query (kept for requestAgentToLeave, not migrated to natalie-api)
const backendApiBaseQuery = axiosBaseQuery({ baseURL: getApiBaseUrl('v1') });

const ACCOUNTLESS_MAX_RETRIES = 3;

/**
 * The stored value a cache draft was made from.
 *
 * A query's `merge` runs inside the cache's own update, so the value it is handed is a draft made
 * from another draft. Only the bottom of that chain is the object the store actually holds, which is
 * what structural sharing has to compare against and hand back.
 */
const storedValue = <T>(value: T): T => (isDraft(value) ? storedValue(original(value as Draft<T>) as T) : value);

/**
 * Whether `incoming` is an older read of the draft than `stored`.
 *
 * A lower revision is older. At the same revision the run tells them apart: starting a generation
 * moves no revision, only its finishing does, so a read taken just before a start and the start's
 * own answer carry the same one. Of those, the draft whose run entered its state later is the newer.
 * A read with no run at all is the older, because a run, once recorded, is never removed. Anything
 * this cannot order is taken as it came.
 */
const isOlderDraft = (incoming: NatalieDraft, stored: NatalieDraft): boolean => {
  if (incoming.revision !== stored.revision) return incoming.revision < stored.revision;

  const storedSince = stored.generation?.since;

  if (storedSince === undefined) return false;
  if (incoming.generation === undefined) return true;

  const incomingSince = incoming.generation.since;

  return incomingSince !== undefined && Date.parse(incomingSince) < Date.parse(storedSince);
};

/**
 * A 4xx from the accountless API is a definitive answer — an unknown event id, or
 * an event that is not Guest-shareable — not a transient fault. RTK Query's
 * default retry condition is status-agnostic, so without this a 404 was retried
 * three times with exponential backoff, leaving the visitor on a spinner for
 * seconds before the page could respond. 5xx and network errors still retry.
 *
 * `RetryOptions` types `maxRetries` and `retryCondition` as mutually exclusive, so
 * the attempt cap is enforced here rather than alongside it. The error arrives as
 * `unknown`, hence the narrowing.
 */
export const retryUnlessClientError = (error: unknown, attempt: number): boolean => {
  const status = (error as FetchBaseQueryError | undefined)?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return attempt <= ACCOUNTLESS_MAX_RETRIES;
};

// Same reasoning as the accountless query: a 4xx here is a definitive answer, not
// a transient fault. Since natalie-api#137 the token mint refuses a full or ended event
// with 400 JOIN_LIMIT_REACHED / EVENT_NOT_ACTIVE, and a status-agnostic retry left the
// visitor on "connecting" through three backoffs before that message could appear.
// 5xx and network errors still retry.
const natalieApiBaseQueryWithRetry = retry(natalieApiBaseQuery, {
  retryCondition: (error, _args, { attempt }) => retryUnlessClientError(error, attempt),
});

// Accountless natalie-api (separate service, no auth required for unauthenticated users)
const accountlessBaseQueryWithRetry = retry(
  axiosBaseQuery({ baseURL: () => configService.getUrl(UrlNames.AccountlessNatalieApi) }),
  { retryCondition: (error, _args, { attempt }) => retryUnlessClientError(error, attempt) }
);

/**
 * Args for the v2 creation contract's writes. Here rather than in
 * `natalieCreateV2.types.ts`, which is the generated contract's alias file and holds
 * only the spec's own shapes — these three are the client's calling convention, and
 * the headers they carry are not part of any schema.
 */

/** The key is minted per submit by the caller, as the repo's other create paths do. */
type CreateNatalieEventV2Args = NatalieEventV2CreateDto & { idempotencyKey: string };

/** `revision` is the one the last response returned; a stale one is refused, not applied. */
type UpdateNatalieEventV2Args = NatalieEventV2UpdateDto & { eventId: string; revision: number };
type GetPublicNatalieEventArgs = { id: string; isTestLink: boolean };
type GetNatalieEventArgs = { id: string; isTestLink: boolean };
/**
 * The test code itself rather than a flag, because the accountless draft read takes it as a PATH
 * segment: on an unauthenticated host the code is the gate, so there is nothing to pass it as
 * except part of the address.
 */
type GetAccountlessNatalieEventArgs = { eventId: string; testInviteCode?: string };
type UpdateNatalieEventV2DraftArgs = NatalieEventV2DraftUpdateDto & { eventId: string; revision: number };

/**
 * The body says how to use the context log — this flow always asks for a `build`, which writes the
 * conversation afresh from the brief. `revision` is `If-Match`.
 */
type GenerateNatalieEventFieldsArgs = NatalieEventV2GenerateDto & { eventId: string; revision: number };

type PublishNatalieEventArgs = { eventId: string; revision: number; note?: string };
type ResetNatalieEventDraftArgs = { eventId: string; revision: number };

/** The event only decides who may ask for a preview; the voice and the text are the whole body. */
type PreviewNatalieVoiceArgs = NatalieVoicePreviewRequestDto & { eventId: string };

/**
 * The voice the event's published version speaks with. `null` is an answer, not a gap: it means no
 * voice is saved, which is what plays River.
 */
export type PublishedNatalieVoice = { voiceId: string | null };

/**
 * The catalogue changes only when natalie-api is deployed, so an organiser who leaves Review and
 * comes back within the hour is not made to wait for it again.
 */
const VOICE_CATALOGUE_CACHE_SECONDS = 60 * 60;

export const natalieEventApi = createApi({
  reducerPath: 'natalieEventApi',
  tagTypes: [
    'NATALIE_EVENT_LIST',
    'NATALIE_EVENT_DETAILS',
    'NATALIE_EVENT_METRICS',
    'NATALIE_EVENT_CONVERSATIONS',
    'NATALIE_REPORT_LIST',
    'NATALIE_REPORT',
    // The v2 draft read. Its own tag rather than NATALIE_EVENT_DETAILS, which keys the
    // v1 participant-facing read of the same id: the two answer different shapes, and a
    // draft write has no business refetching the public detail.
    'NATALIE_DRAFT',
    // The voice catalogue. The same for every caller and every event, and nothing a client does
    // changes it, so nothing invalidates it.
    'NATALIE_VOICES',
  ],
  baseQuery: natalieApiBaseQuery,
  endpoints: (builder) => ({
    /**
     * The sessions list, on v2.
     *
     * A row shows the event's published values once it has been published and its draft's
     * before that, so a self-serve session appears the moment it is created rather than at
     * first publish — which is the whole reason this read had to move: the v1 list cannot
     * see a draft at all.
     *
     * `searchText` matches the name the row shows, so a rename waiting to be published is
     * not findable by its new name until it is.
     */
    getNatalieEventList: builder.query<PaginatedApiResponse<NatalieEventListItem>, GetNatalieEventListApiParams>({
      providesTags: ['NATALIE_EVENT_LIST'],
      query: ({ page, size, searchText, communityId, statuses }) => ({
        url: '/v2/natalie',
        params: { page, size, searchText, communityId, statuses },
      }),
      transformResponse: mapNatalieV2EventListFromApi,
    }),
    /**
     * Reports over this community's Natalie events. Served by natalie-api rather than
     * reporting-api because only it holds the event names, so only it can search by them,
     * order chronologically and return a total count.
     */
    /**
     * One report, with the generation natalie-api is still running (`pendingGeneration`). Signed-in
     * only: the public report page never asks, so share links show no update status.
     */
    getNatalieReport: builder.query<NatalieReport, GetNatalieReportArgs>({
      providesTags: (_result, _error, { reportId }) => [{ type: 'NATALIE_REPORT', id: reportId }],
      query: ({ communityId, reportId }) => ({ url: `/communities/${communityId}/natalie/reports/${reportId}` }),
    }),
    getNatalieReportList: builder.query<PaginatedApiResponse<NatalieReportListItem>, GetNatalieReportListApiParams>({
      providesTags: ['NATALIE_REPORT_LIST'],
      query: ({ communityId, page, size, searchText }) => ({
        url: `/communities/${communityId}/natalie/reports`,
        params: { page, size, searchText },
      }),
    }),
    /**
     * Create a report over one Natalie event. Answers 201 immediately with status GENERATING —
     * insights-api builds the data asynchronously, and its upload to reporting-api is what
     * completes the report.
     *
     * Invalidates the event list as well as the report list: an event row's menu switches between
     * "Create report" and "Go to report" purely on the `reportId` that list returns, so without
     * the second tag the menu would keep offering Create until a refresh.
     */
    createNatalieReport: builder.mutation<NatalieReportListItem, CreateNatalieReportArgs>({
      query: ({ communityId, ...data }) => ({
        url: `/communities/${communityId}/natalie/reports`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['NATALIE_REPORT_LIST', 'NATALIE_EVENT_LIST'],
    }),
    deleteNatalieReport: builder.mutation<void, DeleteNatalieReportArgs>({
      query: ({ communityId, reportId }) => ({
        url: `/communities/${communityId}/natalie/reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NATALIE_REPORT_LIST', 'NATALIE_EVENT_LIST'],
    }),
    /**
     * Ask natalie-api for a fresh version of a report. Answers 202 with the report; insights-api
     * builds the new version asynchronously and its upload lands as a new S3 version under the same
     * reportId, so the share link and PIN are unchanged. 409 `GENERATION_IN_PROGRESS` while one is
     * already running.
     *
     * The reporting-api caches (versions, report state) live in another slice, so
     * `useRegenerateNatalieReport` invalidates those after a 202.
     */
    regenerateNatalieReport: builder.mutation<NatalieReportListItem, RegenerateNatalieReportArgs>({
      query: ({ communityId, reportId }) => ({
        url: `/communities/${communityId}/natalie/reports/${reportId}/regenerate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'NATALIE_REPORT_LIST',
        'NATALIE_EVENT_LIST',
        { type: 'NATALIE_REPORT', id: reportId },
      ],
    }),
    /**
     * Copy an earlier version forward as the new latest. Answers 201 once the copy has landed, so
     * unlike regenerate there is nothing to wait for. Nothing is deleted, and the share link follows
     * the new latest. 409 `GENERATION_IN_PROGRESS` while a generation is running.
     *
     * The reporting-api caches (versions, payload) live in another slice, so
     * `useRestoreNatalieReportVersion` invalidates those.
     */
    restoreNatalieReportVersion: builder.mutation<RestoreNatalieReportVersionResponse, RestoreNatalieReportVersionArgs>(
      {
        query: ({ communityId, reportId, versionId }) => ({
          url: `/communities/${communityId}/natalie/reports/${reportId}/versions/${encodeURIComponent(versionId)}/restore`,
          method: 'POST',
        }),
        invalidatesTags: (_result, _error, { reportId }) => [{ type: 'NATALIE_REPORT', id: reportId }],
      }
    ),
    // The retired wizard's two writes, still on v1 and typed against the v1 DTO they answer with
    // — not against the list row, which is now a v2 shape these have no way to produce.
    createNatalieEvent: builder.mutation<NatalieEventDto, CreateNatalieEventApiParams>({
      query: (data) => ({
        url: '/natalie',
        method: 'POST',
        data: { ...data, communityId: String(data.communityId) },
      }),
      invalidatesTags: ['NATALIE_EVENT_LIST'],
    }),
    updateNatalieEvent: builder.mutation<NatalieEventDto, UpdateNatalieEventApiParams>({
      query: ({ id, ...data }) => ({
        url: `/natalie/${id}`,
        method: 'PUT',
        data: { ...data, communityId: String(data.communityId) },
      }),
      invalidatesTags: (_result, _error, { id }) => ['NATALIE_EVENT_LIST', { type: 'NATALIE_EVENT_DETAILS', id }],
    }),
    /**
     * The registered landing page's read, on v2, and the one place the test link changes which
     * FACE is fetched.
     *
     * A real link reads the live event: what participants get is the published version, and an
     * event that has never been published answers 404 there, the same as an unknown id. A test
     * link reads the draft instead — that is the whole point of a rehearsal, and the draft is
     * the only face a never-published event has.
     *
     * Choosing here, from a query parameter already in the URL, rather than asking the service:
     * the token route resolves test-ness server-side from the invite code, but the landing has
     * to render before any of that, and the code is right there in the address.
     *
     * The draft read is readable by the same callers as the live one — only `prompt`, `context`,
     * `draftBasedOnVersion` and `keptVoiceIds` are held back from a caller who cannot manage the
     * event, and this page reads none of them. So sharing a test link works for the person
     * it was sent to, not only for its author.
     */
    getPublicNatalieEvent: builder.query<NatalieEventPublicDetail, GetPublicNatalieEventArgs>({
      providesTags: (_result, _error, { id }) => [{ type: 'NATALIE_EVENT_DETAILS', id }],
      query: ({ id, isTestLink }) => ({
        url: isTestLink ? `/v2/natalie/${id}/draft` : `/v2/natalie/${id}`,
      }),
      transformResponse: mapNatalieV2PublicEventFromApi,
    }),
    /**
     * The authed single-event read, on v2, and it makes the same draft-or-live choice the
     * landing does: a URL carrying a test code reads the draft, anything else the live event.
     *
     * Every surface behind this can be reached inside a registered test session — the details
     * page, Explore, Feedback — and a never-published event has no live face, so reading it
     * would 404 mid-rehearsal. Screens whose URL never carries a code get `false` from the same
     * lookup, so there is one rule rather than a list of exceptions.
     *
     * `NatalieEventDetail` and `NatalieEventPublicDetail` are the same type, which is why this
     * shares the landing's mapper rather than having one of its own.
     */
    getNatalieEvent: builder.query<NatalieEventDetail, GetNatalieEventArgs>({
      providesTags: (_result, _error, { id }) => [{ type: 'NATALIE_EVENT_DETAILS', id }],
      query: ({ id, isTestLink }) => ({
        url: isTestLink ? `/v2/natalie/${id}/draft` : `/v2/natalie/${id}`,
      }),
      transformResponse: mapNatalieV2PublicEventFromApi,
    }),

    // --- The v2 creation contract, which the self-serve creation flow runs against ---
    //
    // Nothing here mints a session. The rehearsal is a URL built from the test code the draft
    // read already carries, and the join path behind that URL mints the token through
    // `getLiveKitToken` above — which posts to the v2 contract's own
    // `POST /v2/natalie/{eventId}/token`, rendering the prompt server-side and dispatching the
    // agent in the same call, and takes the test code that chooses the draft over the published
    // version. What the room does with the session it is handed is unchanged.

    /**
     * Create the event from the intake answers. Answers immediately with generation
     * reported as running rather than holding the response open for the model call, so
     * the organiser reaches Review without waiting on it.
     *
     * Retry safety is the `Idempotency-Key` header: a repeat of the same key returns the
     * original response rather than creating a second event. The key is minted per submit
     * by the caller, as the repo's other create paths do.
     */
    createNatalieEventV2: builder.mutation<CreatedNatalieDraft, CreateNatalieEventV2Args>({
      query: ({ idempotencyKey, ...data }) => ({
        url: '/v2/natalie',
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        data,
      }),
      transformResponse: mapCreatedNatalieDraftFromApi,
      invalidatesTags: ['NATALIE_EVENT_LIST'],
    }),
    /**
     * The DRAFT face, and the poll target while generation runs.
     *
     * `/draft` rather than the event itself, deliberately. The two reads are different
     * resources: `GET /v2/natalie/{eventId}` serves what participants currently get and
     * answers 404 until the first publish, while this serves what the organiser is
     * editing. Review edits the draft, so it reads the draft — and a newly created event
     * has no live face at all, which is the case the wrong one would have broken first.
     */
    getNatalieEventV2Draft: builder.query<NatalieDraft, string>({
      providesTags: (_result, _error, eventId) => [{ type: 'NATALIE_DRAFT', id: eventId }],
      query: (eventId) => ({
        url: `/v2/natalie/${eventId}/draft`,
      }),
      transformResponse: mapNatalieDraftFromApi,
      /*
       * A read never puts an older draft over a newer one.
       *
       * A read can be overtaken by a write: Setup's brief edit starts a re-read one request before
       * it starts the generation, the server answers that read from before the run, and it lands
       * after the start's own answer has put the run in the cache. Taken as it came, it would tell
       * Review the conversation never started. Which of the two is older is `isOlderDraft`'s call —
       * the revision first, as the Review form already compares the drafts it adopts, then the run —
       * and the start's invalidation re-reads the newer one after it.
       *
       * Defining `merge` turns off the query layer's structural sharing, so it is done here: a read
       * that brings back what is cached keeps the cached object, and a changed one keeps every part
       * it did not change. Without that, each poll would hand Review a new draft and re-render it.
       */
      merge: (cached, incoming) => {
        const stored = storedValue(cached);

        return isOlderDraft(incoming, stored) ? stored : copyWithStructuralSharing(stored, incoming);
      },
    }),
    /**
     * Write the event. A merge-patch: an omitted part is untouched, and within a part an
     * omitted member is left as stored, so only what changed is ever sent. `eventConfig`
     * and `agentConfig` land on the live event; `prompt` lands on the draft, which is what
     * the rehearsal plays and what publishing freezes.
     *
     * `If-Match` carries the revision the caller last read, which is why the mapped
     * response is what the next write reads its revision from. A write that collides with
     * a concurrent one — most often a generation finishing mid-edit — is refused rather
     * than applied, and the refusal names why.
     */
    updateNatalieEventV2: builder.mutation<NatalieDraft, UpdateNatalieEventV2Args>({
      query: ({ eventId, revision, ...data }) => ({
        url: `/v2/natalie/${eventId}`,
        method: 'PUT',
        headers: { 'If-Match': String(revision) },
        data,
      }),
      transformResponse: mapNatalieDraftFromApi,
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'NATALIE_DRAFT', id: eventId }],
      /*
       * A context write lands in the log the moment it is sent, and is taken back out if the
       * service refuses it: a requested change joins the thread, and an edited entry reads as
       * edited.
       *
       * Guarded on `context`, because this route also writes `status`, and those are the only two
       * members it takes. A status write touches no entry, so patching one into the log for it
       * would invent a request the organiser never made. The fields beside the composer are not a
       * concern either way: they write the DRAFT route above.
       *
       * Each item is applied the way the service applies it, in order. One naming a `sequence`
       * replaces that entry's body whole and counts the edit — unless it sends the body already
       * stored, which the service treats as an echo and leaves alone. Appending for it instead
       * would show the organiser the same entry twice until the re-read landed. One without a
       * `sequence` appends.
       *
       * Left in place on success rather than replaced with the response: the write's own answer is
       * not promised to carry the log at all — `context` is optional on it — so upserting one that
       * omitted it would empty the thread. The invalidation's re-read is what replaces the entry
       * with the service's, and this covers exactly the window until it lands.
       *
       * An appended entry is fabricated as far as the shape demands and no further. `sequence` is
       * computed because the log is ordered by it; `version` and `createdBy` are read off the
       * record, and nothing on screen reads either. A new entry has never been edited, so its
       * `editCount` is 0.
       */
      async onQueryStarted({ context, eventId }, { dispatch, queryFulfilled }) {
        if (context === undefined) return;

        const written = dispatch(
          natalieEventApi.util.updateQueryData('getNatalieEventV2Draft', eventId, (draft) => {
            const entries = (draft.context ??= []);

            context.forEach(({ sequence, body }) => {
              if (sequence !== undefined) {
                const edited = entries.find((entry) => entry.sequence === sequence);

                if (edited && !isEqual(edited.body, body)) {
                  edited.body = body;
                  edited.editCount += 1;
                }

                return;
              }

              const next = entries.reduce((highest, entry) => Math.max(highest, entry.sequence), 0) + 1;

              entries.push({
                id: `pending-${next}`,
                sequence: next,
                version: draft.publishedVersion ?? 0,
                createdAt: new Date().toISOString(),
                createdBy: draft.ownerId,
                editCount: 0,
                body,
              });
            });
          })
        );

        try {
          await queryFulfilled;
        } catch {
          written.undo();
        }
      },
    }),
    /**
     * Write the DRAFT: everything a published version freezes.
     *
     * A merge-patch, as above, and a separate operation rather than a flag on the one
     * above because the service treats them as separate resources — sending a versioned
     * block to the live route is a 400 naming the member. Two mutations rather than one
     * with a branch, so the body and the URL cannot drift apart at a call site.
     *
     * The list row carries the event's name and end date, so a draft write can change what
     * the list shows once it is published — but not before, which is why only the draft
     * tag is invalidated here.
     */
    updateNatalieEventV2Draft: builder.mutation<NatalieDraft, UpdateNatalieEventV2DraftArgs>({
      query: ({ eventId, revision, ...data }) => ({
        url: `/v2/natalie/${eventId}/draft`,
        method: 'PUT',
        headers: { 'If-Match': String(revision) },
        data,
      }),
      transformResponse: mapNatalieDraftFromApi,
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'NATALIE_DRAFT', id: eventId }],
    }),
    /**
     * Start a generation. The body names a mode: `build` writes the draft afresh from the
     * brief, with nothing of the current draft shown to the model, and `amend` applies only
     * the entries added or edited since. The contract refuses a call that names neither. So
     * refining is two calls — write the context, then ask for a generation — and retrying a
     * failure is the same call again. There is no separate retry operation.
     *
     * `If-Match` is required here as it is on any write to `prompt`: a generation started
     * from a stale view would otherwise overwrite whatever landed in between, silently.
     *
     * Asynchronous whenever it starts a run: the response reports generation running, and the
     * draft read is the poll target.
     */
    generateNatalieEventFields: builder.mutation<NatalieDraft, GenerateNatalieEventFieldsArgs>({
      query: ({ eventId, revision, ...data }) => ({
        url: `/v2/natalie/${eventId}/generate`,
        method: 'POST',
        headers: { 'If-Match': String(revision) },
        data,
      }),
      transformResponse: mapNatalieDraftFromApi,
      /*
       * A start that got no answer at all is reported as the query layer's own `FETCH_ERROR`,
       * rather than as the 500 the shared base query turns it into. It is the one failure a caller
       * can send again without asking anybody — a start that never arrived started nothing — and
       * whether there was a response is known only here, in `meta`. After this it is just a 500.
       */
      transformErrorResponse: (error, meta) =>
        meta?.response ? error : { status: NO_RESPONSE_STATUS, error: String(error.data) },
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'NATALIE_DRAFT', id: eventId }],
      /*
       * The run the start reported goes into the cache before anything re-reads it, for the reason
       * the publication's response does: the screen the organiser is sent to renders from this
       * entry. Until the invalidation's re-read lands it would otherwise hold the draft as it was
       * before the start — and after an amended brief, that draft reads as a conversation that
       * never started, which Review would say for a round trip after a rebuild that did.
       *
       * The generation and the revision only, not the whole response: an answer to a write is not
       * promised to carry the context log, and the re-read is what settles the rest.
       */
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          const { data: started } = await queryFulfilled;

          dispatch(
            natalieEventApi.util.updateQueryData('getNatalieEventV2Draft', eventId, (draft) => {
              draft.generation = started.generation;
              draft.revision = started.revision;
            })
          );
        } catch {
          // A start that failed changed nothing to put in the cache; the caller handles the failure.
        }
      },
    }),
    /**
     * Freeze the draft as the next version and point the participant link at it. Refused
     * when there is nothing to publish, while a generation is running, and when event
     * configuration is incomplete — and every refusal carries the reason, which is the
     * only place that reason exists.
     */
    publishNatalieEvent: builder.mutation<NatalieDraft, PublishNatalieEventArgs>({
      query: ({ eventId, revision, note }) => ({
        url: `/v2/natalie/${eventId}/publish`,
        method: 'POST',
        headers: { 'If-Match': String(revision) },
        // Optional, and shown in the version list, so an absent note sends no body.
        ...(note ? { data: { note } } : {}),
      }),
      transformResponse: mapNatalieDraftFromApi,
      /*
       * The list and the event's own detail too, not just the draft.
       *
       * A list row carries the event's name and end date, and since those became versioned a
       * draft write does not change what the list shows — publishing is what does. So this is
       * the one operation that can make a row stale, and before it invalidated the list an
       * organiser who renamed and published watched the admin list keep the old name until
       * something else happened to refetch it.
       *
       * `NATALIE_EVENT_DETAILS` is the same argument one level down. `getNatalieEvent` and
       * `getPublicNatalieEvent` provide it, and publishing is exactly what changes what they
       * would answer — an entry read before the publication otherwise serves pre-publication
       * data to whatever mounts next, for as long as the cache keeps it. `updateNatalieEvent`
       * already invalidates it for the same reason; this write was the one that did not.
       */
      invalidatesTags: (_result, _error, { eventId }) => [
        'NATALIE_EVENT_LIST',
        { type: 'NATALIE_DRAFT', id: eventId },
        { type: 'NATALIE_EVENT_DETAILS', id: eventId },
      ],
      /*
       * Put the published draft in the cache before anything re-reads it.
       *
       * A publication's response IS a read of the same record, and the screen an organiser is
       * sent to afterwards renders from this cache entry — it redirects away when it finds no
       * published version. Invalidation alone does not serve that: it starts a re-read, and
       * until that round trip lands every reader still sees the pre-publish draft. The window
       * is invisible against a local fake, which answers in no time, and opens on a real
       * network.
       *
       * The invalidation above stays. This decides what is on screen in the meantime; that
       * decides the record is re-read from the service afterwards.
       */
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        const { data: published } = await queryFulfilled;

        dispatch(natalieEventApi.util.upsertQueryData('getNatalieEventV2Draft', eventId, published));
      },
    }),

    /**
     * Put the draft back to the published version and discard everything staged since.
     *
     * Event configuration, agent configuration AND the prompt, because a version freezes all
     * three — so this is not an undo of the last edit, it is an undo of everything since the last
     * publication. Refinement instructions asked for in that window go dormant: they keep their
     * sequence numbers in the event's `context` and are never sent to the model again, which is
     * what stops a later refinement re-applying what this discarded.
     *
     * Refused on an event that has never been published, since there is nothing to reset to, and
     * while a generation runs, since the generation would rewrite the draft straight afterwards.
     * A reset on a clean draft is a 200 no-op rather than a refusal: the caller asked for the
     * draft to match the published version, and it does.
     *
     * The draft AND the event's own detail. Not the list: a reset changes no published version, so
     * a row's name and end date are what they were, and the contract says the status is unchanged.
     *
     * `NATALIE_EVENT_DETAILS` is less obvious and is the reason this is not draft-only.
     * `getNatalieEvent` and `getPublicNatalieEvent` read `/draft` when the URL carries a test code,
     * which is the rehearsal path — so on a test link they serve the very face this rewrites, and
     * an entry read before the reset would go on serving the discarded draft to whatever mounts
     * next, for as long as the cache keeps it.
     */
    resetNatalieEventDraft: builder.mutation<NatalieDraft, ResetNatalieEventDraftArgs>({
      query: ({ eventId, revision }) => ({
        url: `/v2/natalie/${eventId}/reset`,
        method: 'POST',
        headers: { 'If-Match': String(revision) },
      }),
      transformResponse: mapNatalieDraftFromApi,
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: 'NATALIE_DRAFT', id: eventId },
        { type: 'NATALIE_EVENT_DETAILS', id: eventId },
      ],
      /*
       * The reset draft goes into the cache before anything re-reads it, for the reason the
       * publication does the same: the response IS a read of this record, and the screen is
       * rendering from this entry. Invalidation alone leaves every reader on the pre-reset draft
       * until the round trip lands — invisible against a local fake, a real window on a network,
       * and here it would be the organiser watching the changes they just discarded stay on
       * screen.
       */
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        const { data: reset } = await queryFulfilled;

        dispatch(natalieEventApi.util.upsertQueryData('getNatalieEventV2Draft', eventId, reset));
      },
    }),

    /**
     * Every voice an event can be shown with: the voices the picker offers, in its order, then
     * River, then every other vetted voice, so a picker can name whichever voice an event already
     * uses. natalie-api checks every saved voice against the same catalogue, which is why the
     * client keeps no copy of its own.
     *
     * Returned as sent, because the order is the picker's order.
     */
    getNatalieVoices: builder.query<NatalieVoiceListDto, void>({
      providesTags: ['NATALIE_VOICES'],
      keepUnusedDataFor: VOICE_CATALOGUE_CACHE_SECONDS,
      query: () => ({
        url: '/v2/natalie/voices',
      }),
    }),
    /**
     * Hear a text in one voice: the introduction as it stands on the form, saved or not. Nothing is
     * read from the event or written to it, so there is nothing to invalidate.
     *
     * Synthesis can take up to 20 seconds. `abort()` on the promise the trigger returns cancels the
     * request, and the promise then rejects with `{ name: 'AbortError' }` rather than an API error.
     *
     * No retry here. Every call spends ElevenLabs credit, so whether to try again is the caller's
     * decision. A rejection keeps its HTTP `status`, which matters because the gateway's throttle
     * answers 429 without the V2 envelope.
     *
     * The clip is kept out of Sentry by the store's Redux enhancer. See `store/index.ts`.
     */
    previewNatalieVoice: builder.mutation<NatalieVoicePreviewDto, PreviewNatalieVoiceArgs>({
      query: ({ eventId, ...data }) => ({
        url: `/v2/natalie/${eventId}/voice-preview`,
        method: 'POST',
        data,
      }),
    }),
    /**
     * The voice the event's published version speaks with, read off the live event, whose
     * `agentConfig` is the latest published version. Only for a published event: one never
     * published has no live face and answers 404.
     *
     * A lean read rather than a field on `getNatalieEvent`. That read serves the draft on a test
     * link, so a voice on it would mean the draft's voice in one case and the published one in the
     * other.
     *
     * `NATALIE_EVENT_DETAILS` because this is the same live read `getNatalieEvent` makes, and
     * publishing and resetting already invalidate that tag.
     */
    getNatalieEventV2PublishedVoice: builder.query<PublishedNatalieVoice, string>({
      providesTags: (_result, _error, eventId) => [{ type: 'NATALIE_EVENT_DETAILS', id: eventId }],
      query: (eventId) => ({
        url: `/v2/natalie/${eventId}`,
      }),
      transformResponse: (event: NatalieEventV2Dto) => ({ voiceId: event.agentConfig.voiceId ?? null }),
    }),

    // Per-event conversation metrics (EP13). Owner / org-admin / community-admin only.
    getNatalieEventMetrics: builder.query<NatalieEventMetrics, string>({
      providesTags: (_result, _error, eventId) => [{ type: 'NATALIE_EVENT_METRICS', id: eventId }],
      query: (eventId) => ({
        url: `/natalie/${eventId}/metrics`,
      }),
    }),
    // One page of an event's conversations (EP12), cursor-paginated. Callers page
    // through with `nextCursor` to build the timeline at full timestamp fidelity.
    getNatalieEventConversations: builder.query<NatalieConversationsPage, GetNatalieConversationsArgs>({
      providesTags: (_result, _error, { eventId }) => [{ type: 'NATALIE_EVENT_CONVERSATIONS', id: eventId }],
      query: ({ eventId, cursor }) => ({
        url: '/natalie/conversations',
        params: { eventId, ...(cursor ? { cursor } : {}) },
      }),
    }),
    // Set the manually-entered invited count (EP14).
    setNatalieInvitedCount: builder.mutation<{ eventId: string; invitedCount: number }, SetNatalieInvitedCountArgs>({
      query: ({ eventId, invitedCount }) => ({
        url: `/natalie/${eventId}/invited-count`,
        method: 'PUT',
        data: { invitedCount },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'NATALIE_EVENT_METRICS', id: eventId }],
    }),
    registerToNatalieEvent: builder.mutation<void, RegisterToNatalieEventArgs>({
      query: ({ id }) => ({
        url: `/natalie/${id}/register`,
        method: 'POST',
      }),
    }),
    // "Request a Natalie Event" — get-or-create the requester's own-community create_event
    // event. The server derives identity and authorises the proposed communityId, then returns
    // the create_event event id. The success effect feeds that id into the existing
    // registered-flow join chain; re-activating reuses the same event.
    requestNatalieCreateEvent: builder.mutation<RequestNatalieCreateEventResponse, RequestNatalieCreateEventArgs>({
      query: ({ communityId }) => ({
        url: '/natalie/request',
        method: 'POST',
        data: { communityId: String(communityId) },
      }),
      invalidatesTags: ['NATALIE_EVENT_LIST'],
    }),
    // Announce a submitted intake brief in the client-request channel. Called only AFTER the
    // spreadsheet row is written, because the message is a pointer to that row.
    //
    // No `invalidatesTags`: nothing cached changes. A brief is not a Natalie Session — the
    // session is created by hand off the back of the spreadsheet row, so the event list this API
    // caches has nothing new in it after a submission.
    //
    // The endpoint answers 200 whatever Slack did — a failed post must not fail the submission —
    // so the caller reads `notified` rather than the status to tell an announced submission from
    // an unannounced one.
    notifyNatalieIntakeForm: builder.mutation<NotifyNatalieIntakeFormResponse, NotifyNatalieIntakeFormArgs>({
      query: ({ communityId }) => ({
        url: '/natalie/intake-form/notify',
        method: 'POST',
        // Stringified to match the endpoint's numeric-string contract, as POST /natalie/request
        // does. The whole body: no answers, and no timestamp — Slack stamps the message and the
        // row records its own write time, so there is nothing else the channel needs.
        data: { communityId: String(communityId) },
      }),
    }),
    // requestAgentToLeave — remains on backend-api (not migrated to natalie-api)
    requestAgentToLeave: builder.mutation<void, string>({
      queryFn: async (id, api, extraOptions) => {
        const result = await backendApiBaseQuery({ url: `/natalie/${id}/leave`, method: 'POST' }, api, extraOptions);
        if (result.error) return { error: result.error };
        return { data: result.data as void };
      },
    }),
    /**
     * The registered participant's join, on v2.
     *
     * One call mints the token and dispatches the agent, with the rendered prompt travelling
     * server-to-server as job metadata. v1 put its dispatch config in the token's `roomConfig`,
     * which a LiveKit token signs but does not encrypt — readable by the browser holding it —
     * and a rendered system prompt cannot go there.
     *
     * The caller does not choose the version. Sending the event's `testInviteCode` plays the
     * draft and stamps the conversation `source=test`; anything else plays the published
     * version, and a code that does not match is ignored rather than refused.
     *
     * `agentName` is the only agent override this route honours, and it is load-bearing: it
     * steers dispatch at a named worker, which is how a preview deployment and a locally-run
     * agent get reached. It comes from `?agentName=` on the room's own URL. The greeting, model
     * and system prompt are server authority once an event has a published version, so nothing
     * sends them — and the room's `version`, `slot` and `mode` knobs, which v1 took as query
     * parameters, only ever chose the room UI.
     */
    getLiveKitToken: builder.mutation<GetLiveKitTokenData, GetLiveKitTokenParams>({
      queryFn: async ({ eventId, agentName, testInviteCode }, api, extraOptions) => {
        const ctxParams = extractCtxParams(window.location.search);
        const result = await natalieApiBaseQueryWithRetry(
          {
            url: `/v2/natalie/${eventId}/token`,
            method: 'POST',
            data: {
              // Thread the client-minted visitor id so an authed conversation ties back
              // to its page visit in the funnel (same key path as the accountless flow).
              visitorId: getOrCreateVisitorId(),
              ...(agentName ? { agentConfig: JSON.stringify({ agentName }) } : {}),
              ...(testInviteCode ? { testInviteCode } : {}),
              // The link's ctx_ tags, recorded in the conversation's transcript.
              ...(ctxParams ? { ctxParams } : {}),
            },
          },
          api,
          extraOptions
        );

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as GetLiveKitTokenData };
      },
    }),
    /**
     * The guest landing page's read, on the v2 participant surface.
     *
     * v2 because a Natalie created by the self-serve flow is a v2 event, and the v1 read cannot
     * serve one: since event configuration became versioned, a v2 event's live values are the
     * published ones and the v1 route knows nothing about versions.
     *
     * A real link reads the PUBLISHED version — an event with none answers 404, deliberately the
     * same answer an unknown id gets. A test link reads the draft instead, which is the only face
     * a never-published event has and the whole point of a rehearsal.
     *
     * Same selection the registered landing makes, and from the same signal: the code is in the
     * URL, and the page has to render long before any token exists to resolve it server-side.
     *
     * The code travels in the PATH here, where the registered draft read takes none — that one is
     * gated by who is asking, and this one has no such gate, so the code is what stands in for it.
     */
    getAccountlessNatalieEvent: builder.query<NatalieEventPublicDetail, GetAccountlessNatalieEventArgs>({
      queryFn: async ({ eventId, testInviteCode }, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry(
          {
            url: testInviteCode ? `/v2/natalie/${eventId}/draft/${testInviteCode}` : `/v2/natalie/${eventId}`,
          },
          api,
          extraOptions
        );
        if (result.error) return { error: result.error };
        return { data: mapAccountlessV2EventFromApi(result.data as AccountlessNatalieEventV2ApiResponse) };
      },
    }),
    // Accountless: generate LiveKit token from natalie-api (no auth)
    getAccountlessLiveKitToken: builder.mutation<GetLiveKitTokenData, GetAccountlessLiveKitTokenParams>({
      queryFn: async ({ eventId, inviteCode, agentName }, api, extraOptions) => {
        const ctxParams = extractCtxParams(window.location.search);
        const result = await accountlessBaseQueryWithRetry(
          {
            // v2: the invite code decides which version plays — the real code plays
            // `publishedVersion`, the event's `testInviteCode` plays the draft whether it is
            // published or not, which is the rehearsal path. Server-resolved, so nothing here
            // chooses. The two mints refuse each other's events: a v1 event answers 404 here.
            url: `/v2/natalie/${eventId}/token/${inviteCode}`,
            method: 'POST',
            data: {
              // Thread the client-minted visitor id onto the conversation stub so the
              // visit ties to its conversation (funnel visited→started join key).
              visitorId: getOrCreateVisitorId(),
              // agentName rides in agentConfig (mirrors the authed path); the
              // server uses it as the dispatch target, else its default AGENT_NAME.
              ...(agentName ? { agentConfig: JSON.stringify({ agentName }) } : {}),
              // The link's ctx_ tags, recorded in the conversation's transcript.
              ...(ctxParams ? { ctxParams } : {}),
            },
          },
          api,
          extraOptions
        );
        if (result.error) return { error: result.error };
        return { data: result.data as GetLiveKitTokenData };
      },
    }),
    // Accountless: record a public invite-page visit (funnel "visited" rung, no auth).
    // Fire-and-forget analytics beacon — callers do not await/surface the result; the
    // server 202s on accept and 400s (harmlessly) for a non-joinable event.
    recordAccountlessVisit: builder.mutation<void, RecordAccountlessVisitArgs>({
      queryFn: async ({ eventId, visitorId, accountId }, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry(
          {
            url: `/natalie/${eventId}/visit`,
            method: 'POST',
            // accountId included only for authed visitors, so the funnel can attribute
            // the visit to a known user (server stores it alongside the visitorId).
            data: { visitorId, ...(accountId ? { accountId } : {}) },
          },
          api,
          extraOptions
        );
        if (result.error) return { error: result.error };
        return { data: undefined };
      },
    }),
    // Accountless: schedule a reminder email (no auth)
    scheduleAccountlessReminder: builder.mutation<void, ScheduleAccountlessReminderArgs>({
      queryFn: async ({ eventId, inviteCode, ...body }, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry(
          {
            url: `/natalie/${eventId}/${inviteCode}/reminder`,
            method: 'POST',
            data: body,
          },
          api,
          extraOptions
        );
        if (result.error) return { error: result.error };
        return { data: undefined };
      },
    }),
  }),
});

export const {
  useGetNatalieEventListQuery,
  useGetNatalieReportListQuery,
  useCreateNatalieReportMutation,
  useDeleteNatalieReportMutation,
  useRegenerateNatalieReportMutation,
  useRestoreNatalieReportVersionMutation,
  useGetNatalieReportQuery,
  useCreateNatalieEventMutation,
  useUpdateNatalieEventMutation,
  useGetPublicNatalieEventQuery,
  useGetNatalieEventQuery,
  useCreateNatalieEventV2Mutation,
  useGetNatalieEventV2DraftQuery,
  useUpdateNatalieEventV2DraftMutation,
  useUpdateNatalieEventV2Mutation,
  useGenerateNatalieEventFieldsMutation,
  usePublishNatalieEventMutation,
  useResetNatalieEventDraftMutation,
  useGetNatalieVoicesQuery,
  usePreviewNatalieVoiceMutation,
  useGetNatalieEventV2PublishedVoiceQuery,
  useGetNatalieEventMetricsQuery,
  useLazyGetNatalieEventConversationsQuery,
  useSetNatalieInvitedCountMutation,
  useGetLiveKitTokenMutation,
  useRequestAgentToLeaveMutation,
  useRegisterToNatalieEventMutation,
  useRequestNatalieCreateEventMutation,
  useNotifyNatalieIntakeFormMutation,
  useGetAccountlessNatalieEventQuery,
  useGetAccountlessLiveKitTokenMutation,
  useRecordAccountlessVisitMutation,
  useScheduleAccountlessReminderMutation,
} = natalieEventApi;
